/**
 * Portal-chat client state — Zustand, in-memory only (no localStorage).
 *
 * Each assistant message carries its own ``toolCalls[]`` so the inline
 * chart and the tool-call pill render under the turn that produced
 * them. SSE token deltas merge into the latest assistant message's
 * ``content``.
 */
import { create } from 'zustand';
import type { ChatMessage, ToolCallRecord } from './types';

export type ChatFund =
  | 'mars-fqm'
  | 'mars-dar-nn'
  | 'mars-fidelity-balanced'
  | 'mars-fidelity-conservative';

interface ChatStore {
  open: boolean;
  draft: string;
  messages: ChatMessage[];
  inFlight: boolean;
  error: string | null;
  panelWidth: number;
  panelHeight: number;
  fund: ChatFund;

  setOpen(open: boolean): void;
  setDraft(draft: string): void;
  appendUser(content: string): void;
  startAssistantTurn(): void;
  appendAssistantToken(text: string): void;
  recordToolCall(call: ToolCallRecord): void;
  recordToolResult(id: string, output: Record<string, unknown>): void;
  setInFlight(b: boolean): void;
  setError(err: string | null): void;
  setPanelSize(width: number, height: number): void;
  setFund(fund: ChatFund): void;
  reset(): void;
}

export const usePortalChatStore = create<ChatStore>((set) => ({
  open: false,
  draft: '',
  messages: [],
  inFlight: false,
  error: null,
  // Default panel size — small enough to leave most of the MARS page
  // visible. The user can drag the top-left grip to enlarge.
  panelWidth: 380,
  panelHeight: 560,
  // Default fund the chat reasons about. User overrides via the
  // header dropdown.
  fund: 'mars-fqm',

  setOpen: (open) => set({ open }),
  setDraft: (draft) => set({ draft }),

  appendUser: (content) =>
    set((s) => ({ messages: [...s.messages, { role: 'user', content }] })),

  startAssistantTurn: () =>
    set((s) => ({
      messages: [...s.messages, { role: 'assistant', content: '', toolCalls: [] }],
    })),

  appendAssistantToken: (text) =>
    set((s) => {
      const last = s.messages[s.messages.length - 1];
      if (!last || last.role !== 'assistant') return s;
      const updated = [...s.messages];
      updated[updated.length - 1] = { ...last, content: last.content + text };
      return { messages: updated };
    }),

  recordToolCall: (call) =>
    set((s) => mutateLastAssistant(s.messages, (m) => ({
      ...m, toolCalls: [...(m.toolCalls ?? []), call],
    }))),

  recordToolResult: (id, output) =>
    set((s) => mutateLastAssistant(s.messages, (m) => ({
      ...m,
      toolCalls: (m.toolCalls ?? []).map((c) =>
        c.id === id ? { ...c, status: 'done', output } : c,
      ),
    }))),

  setInFlight: (b) => set({ inFlight: b }),
  setError: (err) => set({ error: err }),
  setPanelSize: (width, height) => set({ panelWidth: width, panelHeight: height }),
  setFund: (fund) => set({ fund }),
  reset: () => set({ messages: [], error: null, draft: '' }),
}));

/** Replace the last assistant message via ``fn``; returns a new messages list. */
function mutateLastAssistant(
  messages: ChatMessage[],
  fn: (m: ChatMessage) => ChatMessage,
): { messages: ChatMessage[] } {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') {
      const updated = [...messages];
      updated[i] = fn(messages[i]);
      return { messages: updated };
    }
  }
  return { messages };
}
